import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook som sporer scroll-retning
 * @param {number} threshold - Minimum scroll-avstand før retning endres (standard 10px)
 * @returns {'up' | 'down' | null} - Scroll-retning eller null ved toppen
 */
const useScrollDirection = (threshold = 10) => {
  const [scrollDirection, setScrollDirection] = useState(null);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isAtTop, setIsAtTop] = useState(true);

  const updateScrollDirection = useCallback(() => {
    const scrollY = window.scrollY;
    
    // Sjekk om vi er på toppen av siden
    if (scrollY < threshold) {
      setIsAtTop(true);
      setScrollDirection(null);
      setLastScrollY(scrollY);
      return;
    }
    
    setIsAtTop(false);
    
    // Beregn scroll-forskjell
    const direction = scrollY > lastScrollY ? 'down' : 'up';
    
    // Kun oppdater retning hvis forskjellen er større enn threshold
    if (Math.abs(scrollY - lastScrollY) > threshold) {
      setScrollDirection(direction);
      setLastScrollY(scrollY);
    }
  }, [lastScrollY, threshold]);

  useEffect(() => {
    // Kun kjør på klient-side
    if (typeof window === 'undefined') return;

    // Initial scroll-posisjon
    setLastScrollY(window.scrollY);
    setIsAtTop(window.scrollY < threshold);

    // Throttle scroll-event for ytelse
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateScrollDirection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [updateScrollDirection, threshold]);

  return { scrollDirection, isAtTop };
};

export default useScrollDirection;
